import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useAIProjectId } from '@/hooks/useAIProjectId';
import { useFS } from '@/hooks/useFS';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAISettings } from '@/hooks/useAISettings';
import { useAppContext } from '@/hooks/useAppContext';
import { AppLayout } from '@/components/AppLayout';
import { OnboardingDialog } from '@/components/OnboardingDialog';
import { Act1Dialog } from '@/components/Act1Dialog';
import { AIErrorMessage } from '@/components/AIErrorMessage';
import { ZipImportDialog } from '@/components/ZipImportDialog';
import { DotAI } from '@/lib/DotAI';
import type { AIMessage } from '@/lib/SessionManager';
import { saveFileToTmp } from '@/lib/fileUtils';
import type OpenAI from 'openai';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ModelSelector } from '@/components/ModelSelector';
import { FileAttachment } from '@/components/ui/file-attachment';
import { ProjectTypeSelector } from '@/components/ProjectTypeSelector';
import { saveProjectType, type ProjectType } from '@/lib/projectType';
import { ArrowUp, FileArchive } from 'lucide-react';
import { useSeoMeta } from '@unhead/react';
import { AfterHoursLogo } from '@/components/AfterHoursLogo';
import { AppShowcase } from '@/components/AppShowcase';
import { useQueryClient } from '@tanstack/react-query';

export default function Index() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [storedPrompt, setStoredPrompt] = useLocalStorage('shakespeare-draft-prompt', '');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAct1Dialog, setShowAct1Dialog] = useState(false);
  const navigate = useNavigate();
  const projectsManager = useProjectsManager();
  const { fs } = useFS();
  const { generateProjectId, isLoading: isGeneratingId } = useAIProjectId();
  const { settings, addRecentlyUsedModel, isLoading: isLoadingSettings } = useAISettings();
  const { config } = useAppContext();
  const [providerModel, setProviderModel] = useState(() => {
    // Initialize with first recently used model if available, otherwise empty
    return settings.recentlyUsedModels?.[0] || '';
  });
  const isMobile = useIsMobile();
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isZipDragOver, setIsZipDragOver] = useState(false);
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [pendingZipFile, setPendingZipFile] = useState<File | null>(null);
  const [aiError, setAIError] = useState<Error | null>(null);
  const [projectType, setProjectType] = useState<ProjectType>('nostr');
  const queryClient = useQueryClient();

  // Check if any providers are configured (treat as configured while loading to avoid flash)
  const hasProvidersConfigured = isLoadingSettings || settings.providers.length > 0;

  useEffect(() => {
    if (!providerModel && settings.recentlyUsedModels?.length) {
      setProviderModel(settings.recentlyUsedModels[0]);
    }
  }, [providerModel, settings.recentlyUsedModels]);

  useSeoMeta({
    title: 'AfterHours AI Builder - Build Something Amazing',
    description: 'Build custom apps with AI assistance using Shakespeare, an AI-powered development environment. Create, code, and deploy projects directly in your browser.',
    ogTitle: 'AfterHours AI Builder - Build Something Amazing',
    ogDescription: 'Build custom apps with AI assistance using Shakespeare, an AI-powered development environment. Create, code, and deploy projects directly in your browser.',
    ogImage: 'https://afterhours.diy/afterhours-og.png',
    ogUrl: 'https://afterhours.diy',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'AfterHours AI Builder - Build Something Amazing',
    twitterDescription: 'Build custom apps with AI assistance using Shakespeare. Create, code, and deploy projects directly in your browser.',
    twitterImage: 'https://afterhours.diy/afterhours-og.png',
    link: [
      { rel: 'canonical', href: 'https://afterhours.diy' },
    ],
  });

  // Restore prompt from local storage on mount
  useEffect(() => {
    if (storedPrompt) {
      setPrompt(storedPrompt);
    }
  }, [storedPrompt]);

  // Check for Act 1 users and show welcome dialog
  useEffect(() => {
    if (localStorage.getItem('selectedNSPAddr')) {
      setShowAct1Dialog(true);
    }
  }, []);

  // Sync prompt with local storage
  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newPrompt = e.target.value;
    setPrompt(newPrompt);
    setStoredPrompt(newPrompt);
  };

  const handleFileSelect = (file: File) => {
    setAttachedFiles(prev => [...prev, file]);
  };

  const handleFileRemove = (fileToRemove: File) => {
    setAttachedFiles(prev => prev.filter(file => file !== fileToRemove));
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only reset drag state if we're actually leaving the container
    // This prevents flickering when dragging over child elements
    const container = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;

    if (!container.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isCreating || isGeneratingId || !providerModel.trim()) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Add all files without validation
    setAttachedFiles(prev => [...prev, ...files]);
  };

  // ZIP file drag-drop handlers for the entire page
  const handlePageDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if any dragged file is a ZIP
    const hasZipFile = Array.from(e.dataTransfer.items).some(item => {
      if (item.kind === 'file') {
        const type = item.type;
        return type === 'application/zip' || type === 'application/x-zip-compressed';
      }
      return false;
    });

    if (hasZipFile) {
      setIsZipDragOver(true);
    }
  }, []);

  const handlePageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only reset if we're leaving the main container
    const container = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;
    if (!container.contains(relatedTarget)) {
      setIsZipDragOver(false);
    }
  }, []);

  const handlePageDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsZipDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const zipFile = files.find(file => file.name.toLowerCase().endsWith('.zip'));

    if (zipFile) {
      // Set pending ZIP file and open dialog
      setPendingZipFile(zipFile);
      setZipDialogOpen(true);
    }
  }, []);

  const handleZipImport = async (file: File, overwrite?: boolean, projectId?: string) => {
    const project = await projectsManager.importProjectFromZip(file, projectId, overwrite);
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    setPendingZipFile(null);
    navigate(`/project/${project.id}`);
  };

  const handleZipDialogOpenChange = (open: boolean) => {
    setZipDialogOpen(open);
    if (!open) {
      setPendingZipFile(null);
    }
  };

  // Handle keyboard shortcuts (physical keyboards only)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      // On mobile devices, always allow Enter to create new lines
      // since there's no Shift key available for multi-line input
      if (isMobile) {
        return;
      }

      if (e.shiftKey) {
        // Shift+Enter on desktop: Allow new line (default behavior)
        return;
      }

      // Enter without Shift on desktop: Submit only if no newlines exist in prompt and model is selected
      if (!prompt.includes('\n') && providerModel.trim()) {
        e.preventDefault();
        handleCreateProject();
      }
      // If prompt contains newlines or no model selected, allow Enter to create new line (default behavior)
    }
  };

  // Handle textarea click - show onboarding if no providers configured
  const handleTextareaClick = () => {
    if (!hasProvidersConfigured) {
      setShowOnboarding(true);
    }
  };

  // Error handlers
  const handleErrorDismiss = () => {
    setAIError(null);
  };

  const handleErrorNewChat = () => {
    // Clear current prompt and start fresh
    setPrompt('');
    setStoredPrompt('');
    setAttachedFiles([]);
    setAIError(null);
  };

  const handleErrorOpenModelSelector = () => {
    // Focus on model selector or open AI settings if no providers
    if (!hasProvidersConfigured) {
      setShowOnboarding(true);
    }
    setAIError(null);
  };

  const handleCreateProject = async () => {
    if (!prompt.trim() || !providerModel.trim()) return;

    // Clear stored prompt when creating project
    setStoredPrompt('');

    setIsCreating(true);
    try {
      // Use AI to generate project ID
      const projectId = await generateProjectId(providerModel, prompt.trim());

      // Add model to recently used when creating project with AI
      addRecentlyUsedModel(providerModel.trim());

      // Create project with AI-generated ID
      // Both project types use the same template - the system prompt handles the difference
      // Pass custom agents file content if configured
      const project = await projectsManager.createProject(prompt.trim(), config.projectTemplate, projectId, {
        agentsFileContent: config.agentsFileTemplate,
      });

      // Save the selected project type so the correct system prompt is used
      await saveProjectType(fs, project.path, projectType);

      // Build message content as text parts (same as ChatPane)
      const contentParts: Array<OpenAI.Chat.Completions.ChatCompletionContentPartText> = [];

      // Add user input as text part if present
      if (prompt.trim()) {
        contentParts.push({
          type: 'text',
          text: prompt.trim()
        });
      }

      // Process attached files and add as separate text parts
      if (attachedFiles.length > 0) {
        const filePromises = attachedFiles.map(async (file) => {
          try {
            const savedPath = await saveFileToTmp(fs, file);
            return `Added file: ${savedPath}`;
          } catch (error) {
            console.error('Failed to save file:', error);
            return `Failed to save file: ${file.name}`;
          }
        });

        const fileResults = await Promise.all(filePromises);

        // Add each file as a separate text part
        fileResults.forEach(fileResult => {
          contentParts.push({
            type: 'text',
            text: fileResult
          });
        });
      }

      // Store the initial message in chat history using DotAI
      const dotAI = new DotAI(fs, `/projects/${project.id}`);
      const sessionName = DotAI.generateSessionName();

      // Create initial message with content parts (same format as ChatPane)
      const initialMessage: AIMessage = {
        role: 'user',
        content: contentParts.length === 1 ? contentParts[0].text : contentParts
      };
      await dotAI.setHistory(sessionName, [initialMessage]);

      // Clear attached files after successful creation
      setAttachedFiles([]);

      // Navigate to the project with autostart parameter and model
      const searchParams = new URLSearchParams({
        autostart: 'true',
        build: 'true',
        ...(providerModel.trim() && { model: providerModel.trim() })
      });
      navigate(`/project/${project.id}?${searchParams.toString()}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      setAIError(error instanceof Error ? error : new Error("An unexpected error occurred"));
    } finally {
      setIsCreating(false);
    }
  };

  const headerContent = null;

  return (
    <>
      {/* Ambient Glow - fixed to viewport, across the top of the page */}
      <div className="fixed top-0 left-0 right-0 z-0 overflow-visible pointer-events-none h-[600px]" aria-hidden="true">
        {/* Left side glow */}
        <div className="absolute ambient-glow animate-glow-pulse w-[500px] h-[500px] md:w-[700px] md:h-[700px] top-0 left-0" />
        {/* Center glow */}
        <div className="absolute ambient-glow animate-glow-pulse-delayed w-[600px] h-[600px] md:w-[900px] md:h-[900px] -top-20 left-1/2 -translate-x-1/2" />
        {/* Right side glow */}
        <div className="absolute ambient-glow animate-glow-pulse w-[500px] h-[500px] md:w-[700px] md:h-[700px] top-0 right-0 animation-delay-300" />
      </div>

      <AppLayout headerContent={headerContent}>
        <div
          className="relative min-h-full"
          onDragEnter={handlePageDragEnter}
          onDragOver={handlePageDragOver}
          onDragLeave={handlePageDragLeave}
          onDrop={handlePageDrop}
        >
          {/* ZIP Drop Overlay */}
          {isZipDragOver && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm border-2 border-dashed border-primary rounded-lg pointer-events-none">
              <div className="text-center space-y-4">
                <FileArchive className="h-16 w-16 mx-auto text-primary" />
                <div>
                  <p className="text-xl font-semibold text-primary">{t('dropZipToImport')}</p>
                  <p className="text-sm text-muted-foreground">{t('releaseToCreateProject')}</p>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-4xl mx-auto relative">

          <div className="text-center mb-8 md:mb-12">
            {/* Animated Logo */}
            <div className="mb-3 md:mb-4 animate-on-load animate-fade-in-scale">
              <div className="relative inline-block">
                <AfterHoursLogo className="w-20 h-20 md:w-28 md:h-28 mx-auto animate-float" />
                {/* Subtle glow effect behind logo */}
                <div className="absolute inset-0 -z-10 w-20 h-20 md:w-28 md:h-28 mx-auto bg-primary/15 rounded-full blur-3xl animate-pulse-subtle" />
              </div>
            </div>
            {/* Animated Title */}
            <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4 tracking-tight animate-on-load animate-fade-in-up animation-delay-200">
              <span className="text-primary">
                Ship a Working App in Under 2 Minutes
              </span>
            </h1>
            {/* Animated Subheading */}
            <p className="text-lg md:text-xl text-foreground/90 mb-4 animate-on-load animate-fade-in-up animation-delay-300 max-w-3xl mx-auto">
              Most AI builders give you broken UI-only results. AfterHours delivers actual working apps in one prompt, without a need for a database.
            </p>
            {/* Animated Tagline */}
            <p className="text-base md:text-lg text-muted-foreground animate-on-load animate-fade-in-up animation-delay-400">
              {t('whatToBuild')}
            </p>
          </div>

          <div className="mb-8 md:mb-12 animate-on-load animate-fade-in-up animation-delay-500">
            {/* Project Type Selector */}
            <ProjectTypeSelector
              value={projectType}
              onChange={setProjectType}
              className="mb-4"
            />
            {/* Error Message - shows when there are errors */}
            {aiError && (
              <div className="mb-4">
                <AIErrorMessage
                  error={aiError}
                  onDismiss={handleErrorDismiss}
                  onNewChat={handleErrorNewChat}
                  onOpenModelSelector={handleErrorOpenModelSelector}
                  providerModel={providerModel}
                />
              </div>
            )}

            {/* Chat Input Container - modern glassmorphism style */}
            <div
              className={`relative rounded-2xl border-2 border-border bg-card/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/50 ${isDragOver ? 'border-primary bg-primary/5 ring-2 ring-primary/20 scale-[1.01]' : ''
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Textarea
                placeholder={
                  !hasProvidersConfigured
                    ? t('examplePrompt')
                    : !providerModel.trim()
                      ? t('selectModelToDescribe')
                      : t('examplePrompt')
                }
                value={prompt}
                onChange={handlePromptChange}
                onKeyDown={handleKeyDown}
                onClick={handleTextareaClick}
                className="min-h-[72px] md:min-h-[120px] max-h-[120px] md:max-h-64 resize-none border-0 bg-transparent px-4 py-3 pb-16 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
                disabled={isCreating || isGeneratingId || (hasProvidersConfigured && !providerModel.trim())}
                rows={isMobile ? 2 : 4}
                style={{
                  height: 'auto',
                  minHeight: isMobile ? '72px' : '120px'
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  // Mobile: max 120px, Desktop: max 256px
                  const maxHeight = isMobile ? 120 : 256;
                  target.style.height = Math.min(target.scrollHeight, maxHeight) + 'px';
                }}
              />

              {/* Bottom Controls Row */}
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-end gap-4 overflow-hidden">
                {/* File Attachment */}
                <FileAttachment
                  className="mr-auto"
                  onFileSelect={handleFileSelect}
                  onFileRemove={handleFileRemove}
                  selectedFiles={attachedFiles}
                  disabled={isCreating || isGeneratingId}
                  multiple={true}
                />

                {/* Model Selector - always show to allow configuration */}
                <div className="overflow-hidden">
                  <ModelSelector
                    value={providerModel}
                    onChange={setProviderModel}
                    className="w-full"
                    disabled={isCreating || isGeneratingId}
                    placeholder={t('chooseModel')}
                  />
                </div>

                {/* Create Project Button */}
                <Button
                  onClick={handleCreateProject}
                  disabled={
                    !prompt.trim() ||
                    isCreating ||
                    isGeneratingId ||
                    (hasProvidersConfigured && !providerModel.trim())
                  }
                  size="sm"
                  className="size-8 [&_svg]:size-5 rounded-full p-0"
                >
                  {isCreating || isGeneratingId ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                  ) : (
                    <ArrowUp />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* App Showcase */}
          <AppShowcase />
        </div>
      </AppLayout>

      {/* Onboarding Dialog */}
      <OnboardingDialog
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
      />

      {/* Act 1 Welcome Dialog */}
      <Act1Dialog
        open={showAct1Dialog}
        onOpenChange={setShowAct1Dialog}
      />

      {/* ZIP Import Dialog - triggered by drag-drop */}
      <ZipImportDialog
        onImport={handleZipImport}
        open={zipDialogOpen}
        onOpenChange={handleZipDialogOpenChange}
        initialFile={pendingZipFile}
      />
    </>
  );
}